import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useGetMe,
  useGetInboundEmail,
  useSetInboundEmailReadState,
  useAiReplyDraft,
  useListInboundMailboxes,
  useSendEmail,
  getGetInboundEmailQueryKey,
  getListInboundEmailsQueryKey,
  getListInboundMailboxesQueryKey,
  getListEmailLogsQueryKey,
  type InboundEmailDetail,
  type InboundThreadMessage,
  type SendEmailInput,
  type AiReplyDraftInputTone,
} from "@workspace/api-client-react";
import { REPLY_TONE_VALUES, REPLY_TONE_LABELS } from "@/lib/enumValues";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Loader2,
  Mail,
  MailOpen,
  Reply,
  Send,
  Sparkles,
  Paperclip,
  ArrowDownRight,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ShieldAlert,
} from "lucide-react";
import {
  apiErrorMessage,
  apiErrorStatus,
} from "@/lib/apiError";
import { useReplyDraftPersistence } from "@/lib/replyDraftPersistence";
import { AttachmentsField } from "@/components/attachments-field";
import { VerifiedSenderBadge } from "@/components/VerifiedSenderBadge";
import { FailedVerificationWarning } from "@/components/FailedVerificationWarning";
import type { VerificationDetails } from "@/components/FailedVerificationWarning";
import type { VerificationStatus } from "@/components/VerifiedSenderBadge";
import { useTrustedSenderCheck } from "@/hooks/useTrustedSenderCheck";
import type { ComposeAttachment } from "@/lib/attachments";
import { rewriteLinksForPreview } from "@/lib/previewHtml";
import { AiRateLimitInline } from "@/components/AiRateLimitInline";
import { useAiRateLimitUpdater } from "@/lib/aiRateLimit";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type ReplyTone = AiReplyDraftInputTone;

export default function InboxDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const id = parseInt(params.id ?? "", 10);

  const meQuery = useGetMe();
  const me = meQuery.data;

  const emailQuery = useGetInboundEmail(id, {
    query: {
      queryKey: getGetInboundEmailQueryKey(id),
      enabled: Number.isFinite(id) && id > 0 && me?.isAdmin === true,
    },
  });

  const setReadMutation = useSetInboundEmailReadState();
  const aiReplyMutation = useAiReplyDraft();
  const sendMutation = useSendEmail();
  const aiRateLimit = useAiRateLimitUpdater();
  const mailboxesQuery = useListInboundMailboxes(undefined, {
    query: {
      enabled: me?.isAdmin === true,
      queryKey: getListInboundMailboxesQueryKey(),
    },
  });

  // Inline reply panel state. Lives next to the message body so power
  // users can fire off a quick acknowledgement without bouncing
  // through the Compose page. The Compose deep-link still exists as a
  // fallback when they need template variables / attachments / preview.
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySubject, setReplySubject] = useState("");
  const [replyBody, setReplyBody] = useState("");
  // Track whether the body was AI-drafted (vs user-typed) so we can
  // surface a tiny inline indicator without forcing a re-render hook.
  const [replyAiDrafted, setReplyAiDrafted] = useState(false);
  // Selected tone for the AI draft. "auto" = let the model pick.
  const [replyTone, setReplyTone] = useState<ReplyTone>("auto");
  // True when the user hits "Generate" again with an existing AI body
  // already in the panel — gates a confirm dialog so we don't silently
  // discard their edits.
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  // Set after a draft comes back with `repDefaultsMissing: true` so
  // we can show a one-line nudge under the body. Reset on each draft.
  const [showRepDefaultsNudge, setShowRepDefaultsNudge] = useState(false);
  // Optional file attachments — mirrors Compose's attachment widget
  // (PDF + common image formats, ~25 MB combined). Stored as base64 so
  // the existing /api/send `attachments` field can take them as-is.
  const [replyAttachments, setReplyAttachments] = useState<ComposeAttachment[]>(
    [],
  );

  // Per-message collapsed/expanded state for the thread view. We track
  // expanded keys (kind-id) explicitly; null means "haven't seeded
  // defaults yet" so the first effect below can pick a sensible
  // default once the email loads. Gmail-style default: only the most
  // recent message is expanded — older ones collapse to a one-liner
  // showing sender, snippet, and timestamp.
  const [expandedThreadKeys, setExpandedThreadKeys] = useState<
    Set<string> | null
  >(null);
  useEffect(() => {
    if (expandedThreadKeys !== null) return;
    const data = emailQuery.data;
    if (!data) return;
    const messages = data.thread.messages;
    if (messages.length === 0) {
      setExpandedThreadKeys(new Set());
      return;
    }
    const last = messages[messages.length - 1]!;
    setExpandedThreadKeys(new Set([`${last.kind}-${last.id}`]));
  }, [emailQuery.data, expandedThreadKeys]);

  // Optimistic outbound replies. We append a temporary InboundThreadMessage
  // to the cached inbox-detail thread the moment the user clicks "Send
  // reply" so the conversation feels instant on slow connections, then
  // swap it for the real server-confirmed entry on the next refetch (or
  // remove it on failure). Each optimistic message gets a unique negative
  // id — real `email_logs.id` values are always positive, so there's no
  // chance of collision with a future real reply, and React's reconciler
  // can match keys cleanly across renders.
  const optimisticIdRef = useRef(-1);
  const [pendingOptimisticIds, setPendingOptimisticIds] = useState<Set<number>>(
    () => new Set(),
  );

  const toggleThreadMessage = (key: string) => {
    setExpandedThreadKeys((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Auto-mark as read once on mount when the email loads (and was unread).
  const markedReadRef = useRef(false);
  useEffect(() => {
    if (markedReadRef.current) return;
    const data = emailQuery.data;
    if (!data || data.readAt !== null) return;
    markedReadRef.current = true;
    setReadMutation
      .mutateAsync({ id: data.id, data: { read: true } })
      .then(() => {
        // Optimistically update caches so the list view + nav badge
        // refresh without a full refetch on next visit.
        queryClient.invalidateQueries({
          queryKey: getGetInboundEmailQueryKey(data.id),
        });
        queryClient.invalidateQueries({
          queryKey: getListInboundEmailsQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getListInboundMailboxesQueryKey(),
        });
      })
      .catch(() => {
        // Non-fatal — the user can still toggle manually.
      });
  }, [emailQuery.data, setReadMutation, queryClient]);

  const [aiPending, setAiPending] = useState(false);

  // Persist the inline reply panel to the server so a half-typed reply
  // survives a page refresh. Wires `replySubject`/`replyBody`/
  // `replyAiDrafted` into the per-user `reply_drafts` row keyed off the
  // visible URL id (not the synthetic conversation thread id elsewhere
  // in the inbox) so opening any other message in the same thread does
  // NOT inherit a stranger's draft.
  const draftRef = String(id);
  const replyDraft = useReplyDraftPersistence({
    kind: "inbound",
    ref: draftRef,
    enabled: Number.isFinite(id) && id > 0 && me?.isAdmin === true,
    open: replyOpen,
    subject: replySubject,
    body: replyBody,
    isAiDrafted: replyAiDrafted,
    onRestore: (draft) => {
      if (!draft) return;
      // Only restore when we actually have something to restore. An
      // empty saved row would just blank the panel for no reason.
      if (draft.subject.length === 0 && draft.body.length === 0) return;
      setReplySubject(draft.subject);
      setReplyBody(draft.body);
      setReplyAiDrafted(draft.isAiDrafted);
      setReplyOpen(true);
    },
  });

  if (meQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!me?.isAdmin) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-muted-foreground p-12">
        <ShieldAlert className="w-12 h-12 opacity-40" />
        <p className="font-medium text-base">Access restricted</p>
        <p className="text-sm text-center max-w-xs">
          The shared inbox is only accessible to workspace administrators.
        </p>
        <Button variant="outline" onClick={() => setLocation("/app")}>
          Go to dashboard
        </Button>
      </div>
    );
  }

  if (emailQuery.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (emailQuery.isError || !emailQuery.data) {
    return (
      <div className="p-12 text-center text-muted-foreground">
        <p className="font-medium">Inbound email not found.</p>
        <Button
          variant="link"
          onClick={() => setLocation("/app/inbox")}
          className="mt-2"
        >
          Back to inbox
        </Button>
      </div>
    );
  }

  const email = emailQuery.data;
  const isUnread = email.readAt === null;

  const handleToggleRead = async () => {
    try {
      const next = isUnread;
      await setReadMutation.mutateAsync({
        id: email.id,
        data: { read: next },
      });
      queryClient.invalidateQueries({
        queryKey: getGetInboundEmailQueryKey(email.id),
      });
      queryClient.invalidateQueries({
        queryKey: getListInboundEmailsQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getListInboundMailboxesQueryKey(),
      });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to update read state"));
    }
  };

  // Default Re: subject for replies (idempotent — never doubles up).
  const defaultReplySubject = (() => {
    const base = email.subject || "(no subject)";
    return /^re:\s*/i.test(base) ? base : `Re: ${base}`;
  })();

  // Default quoted body shown when the inline panel opens manually.
  const defaultQuotedBody = (() => {
    const quoted = (email.textBody ?? "")
      .split("\n")
      .map((l) => `> ${l}`)
      .join("\n");
    return `\n\nOn ${new Date(email.receivedAt).toLocaleString()}, ${email.fromName ? `${email.fromName} ` : ""}<${email.fromAddress}> wrote:\n${quoted}`;
  })();

  const goCompose = (subject: string, body: string) => {
    const params = new URLSearchParams();
    params.set("to", email.fromAddress);
    params.set("subject", subject);
    params.set("body", body);
    params.set("from-mailbox", email.mailboxAddress);
    // Carry the inbound mailbox into Compose's Reply-To input so the
    // round-trip from "Reply in Compose" matches the inline-reply path
    // (which sets `replyToOverride: email.mailboxAddress` on /send).
    // Without this, picking "Reply in Compose" would silently send
    // replies back through the verified shared sender instead of the
    // per-mailbox address the recipient originally wrote to.
    params.set("reply-to", email.mailboxAddress);
    params.set("inboundEmailId", String(email.id));
    setLocation(`/app/compose?${params.toString()}`);
  };

  // "Reply in Compose" — the original deep-link path, kept as a
  // fallback for power users who want template variables / attachments.
  // Quotes the LATEST inbound message in the thread, not necessarily
  // the one whose id is in the URL — that's the expected gmail-style
  // behavior when opening a thread.
  const handleReplyInCompose = () => {
    const latestInbound = [...email.thread.messages]
      .reverse()
      .find((m) => m.kind === "inbound");
    const source = latestInbound ?? {
      subject: email.subject,
      textBody: email.textBody,
      sentAt: email.receivedAt,
      fromName: email.fromName,
      fromAddress: email.fromAddress,
    };
    const subject = /^re:\s*/i.test(source.subject)
      ? source.subject
      : `Re: ${source.subject || "(no subject)"}`;
    const quoted = (source.textBody ?? "")
      .split("\n")
      .map((l) => `> ${l}`)
      .join("\n");
    const body = `\n\nOn ${new Date(source.sentAt).toLocaleString()}, ${source.fromName ? `${source.fromName} ` : ""}<${source.fromAddress}> wrote:\n${quoted}`;
    goCompose(subject, body);
  };

  // Open the inline reply panel pre-filled with a quoted body. If the
  // user already opened it (and possibly typed), don't clobber their
  // draft — just scroll the panel into view. We only seed defaults
  // when the form is genuinely blank: if the user typed something
  // and then collapsed via Cancel, their text is still in
  // `replySubject` / `replyBody` (Cancel intentionally only hides the
  // panel) and reopening should restore it untouched.
  const openInlineReply = () => {
    if (!replyOpen) {
      const isBlank = replySubject.length === 0 && replyBody.length === 0;
      if (isBlank) {
        setReplySubject(defaultReplySubject);
        setReplyBody(defaultQuotedBody);
        setReplyAiDrafted(false);
      }
      setReplyOpen(true);
    }
    // Defer to next paint so the panel exists in the DOM.
    setTimeout(() => {
      document
        .getElementById("inline-reply-panel")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
  };

  // AI-drafted reply now lands in the inline panel instead of jumping
  // to Compose. Power users can still get to Compose via the
  // "Reply in Compose" deep-link if they want to add attachments etc.
  const runAiReplyDraft = async () => {
    setAiPending(true);
    try {
      const draft = await aiReplyMutation.mutateAsync({
        data: {
          inboundEmailId: email.id,
          ...(replyTone !== "auto" ? { tone: replyTone } : {}),
        },
      });
      // Push the freshly-returned remaining/max into the dashboard
      // cache so the inline rate-limit banner ticks down without a
      // round-trip to /summary.
      aiRateLimit.updateFromResponse({
        rateLimitRemaining: draft.rateLimitRemaining,
        rateLimitMax: draft.rateLimitMax,
        rateLimitResetAt: new Date(draft.rateLimitResetAt).toISOString(),
      });
      setReplySubject(draft.subject);
      setReplyBody(draft.body);
      setReplyAiDrafted(true);
      setShowRepDefaultsNudge(draft.repDefaultsMissing === true);
      setReplyOpen(true);
      setTimeout(() => {
        document
          .getElementById("inline-reply-panel")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    } catch (e) {
      // Mirror rate-limit info from the 429 body into the cache so
      // the inline banner immediately flips to its exhausted state.
      aiRateLimit.updateFromError(e);
      const status = apiErrorStatus(e);
      if (status === 429) {
        toast.error(
          `AI rate limit reached. ${apiErrorMessage(e, "Try again later.")}`,
        );
      } else {
        toast.error(apiErrorMessage(e, "AI reply failed"));
      }
    } finally {
      setAiPending(false);
    }
  };

  // Top-level handler for the "Draft reply" button. If the panel
  // is already open with an AI-drafted body (or any user-typed body
  // the user hasn't cleared), prompt before clobbering it.
  const handleAiReply = () => {
    const hasUserContent =
      replyOpen &&
      (replyBody.trim().length > 0 || replySubject.trim().length > 0);
    if (hasUserContent) {
      setConfirmRegenerate(true);
      return;
    }
    void runAiReplyDraft();
  };

  const ensureMarkedRead = async () => {
    if (!isUnread) return;
    try {
      await setReadMutation.mutateAsync({
        id: email.id,
        data: { read: true },
      });
    } catch {
      // Non-fatal — mark-read is a nicety after a successful send.
    }
  };

  const handleSendInlineReply = async () => {
    const subject = replySubject.trim();
    const body = replyBody.trim();
    if (!subject) {
      toast.error("Subject is required");
      return;
    }
    if (!body) {
      toast.error("Reply body is required");
      return;
    }

    const attachmentPayload =
      replyAttachments.length > 0
        ? replyAttachments.map((a) => ({
            filename: a.filename,
            content: a.content,
            contentType: a.contentType,
          }))
        : undefined;

    const replyHtml = `<pre style="font-family: -apple-system, system-ui, sans-serif; white-space: pre-wrap; padding: 16px; line-height: 1.5;">${body
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")}</pre>`;

    const payload: SendEmailInput = {
      templateId: null,
      to: email.fromAddress,
      subject,
      // Plain-text reply: send as `text` and let the server convert to a
      // minimal HTML body. Inline replies are deliberately plain so they
      // thread cleanly in Gmail / Outlook.
      text: body,
      html: replyHtml,
      // Send recipient replies back to the inbound mailbox (e.g.
      // sales@example.com), not the global Resend sender, so the
      // conversation stays on the right inbox.
      replyToOverride: email.mailboxAddress,
      // Threading: hand the server the inbound id so it derives the
      // In-Reply-To / References headers AND stamps the outbound
      // email_log with the inbound's thread_id. Stamping the
      // thread_id is what lets the new reply appear inside the
      // conversation when we refetch the thread below — passing the
      // raw inReplyTo / references strings only sets the outgoing
      // headers and leaves email_logs.thread_id null, which would
      // make the reply invisible to the inbox detail thread query.
      inReplyToInboundEmailId: email.id,
      ...(attachmentPayload ? { attachments: attachmentPayload } : {}),
    };

    // ── Optimistic UI ────────────────────────────────────────────────
    // Append a temporary outbound message to the cached thread before
    // the network request goes out so the user sees their reply land
    // immediately. Uses a unique negative id so it can be matched and
    // removed on failure without colliding with any real email_log row.
    const optimisticId = optimisticIdRef.current--;
    const optimisticMessage: InboundThreadMessage = {
      kind: "outbound",
      id: optimisticId,
      fromAddress: email.mailboxAddress,
      fromName: null,
      toAddress: email.fromAddress,
      subject,
      textBody: body,
      htmlBody: replyHtml,
      attachments: replyAttachments.map((a) => ({
        filename: a.filename,
        contentType: a.contentType,
      })),
      providerMessageId: null,
      verificationStatus: null,
      verificationDetails: null,
      sentAt: new Date().toISOString(),
      readAt: null,
    };

    const detailKey = getGetInboundEmailQueryKey(email.id);
    queryClient.setQueryData<InboundEmailDetail>(detailKey, (prev) =>
      prev
        ? {
            ...prev,
            thread: {
              ...prev.thread,
              messages: [...prev.thread.messages, optimisticMessage],
            },
          }
        : prev,
    );
    setPendingOptimisticIds((prev) => {
      const next = new Set(prev);
      next.add(optimisticId);
      return next;
    });
    // Auto-expand the optimistic entry — the user just wrote it, they
    // expect to see it open in the thread the same way it would after
    // the refetch lands.
    setExpandedThreadKeys((prev) => {
      const next = new Set(prev ?? []);
      next.add(`outbound-${optimisticId}`);
      return next;
    });

    // Snapshot the draft so we can restore it on failure, then close
    // the panel right away — clicking Send and watching the form linger
    // is exactly the latency this task is about hiding.
    const draftSnapshot = {
      subject: replySubject,
      body: replyBody,
      aiDrafted: replyAiDrafted,
      attachments: replyAttachments,
    };
    setReplyOpen(false);
    setReplyBody("");
    setReplySubject("");
    setReplyAiDrafted(false);
    setShowRepDefaultsNudge(false);
    setReplyAttachments([]);

    try {
      const sent = await sendMutation.mutateAsync({ data: payload });
      toast.success("Reply sent");

      // Clear the saved draft now that the reply has actually gone out.
      // Fire-and-forget — failure to delete the draft is recoverable
      // (the user can hit Discard manually) and shouldn't block the
      // success path. We only delete on success: a failed send rolls
      // back the optimistic UI and restores the form, and the draft
      // row stays in place so the user can retry without retyping.
      void replyDraft.clearDraft();

      // Pre-expand the real id so when the refetch swaps the optimistic
      // entry for the server-confirmed one, the new message stays open
      // instead of snapping shut (the default-expansion seeding effect
      // only runs once on first load).
      setExpandedThreadKeys((prev) => {
        const next = new Set(prev ?? []);
        next.add(`outbound-${sent.id}`);
        return next;
      });

      // Drop the "sending…" indicator now that the server accepted the
      // reply. The optimistic entry stays in the cache until the
      // refetch lands, but visually it's already a confirmed message —
      // no flicker between optimistic and real.
      setPendingOptimisticIds((prev) => {
        if (!prev.has(optimisticId)) return prev;
        const next = new Set(prev);
        next.delete(optimisticId);
        return next;
      });

      // Refetch the inbox detail so the optimistic entry is replaced
      // by the real one (with the canonical id, providerMessageId,
      // server-stamped sentAt, etc.). The cache replacement is atomic
      // so there's no double-render.
      queryClient.invalidateQueries({ queryKey: detailKey });

      // Mark the inbound message read after a successful reply so
      // the inbox queue stays accurate. Don't await — the toast +
      // collapse happens immediately either way.
      void ensureMarkedRead().then(() => {
        queryClient.invalidateQueries({ queryKey: detailKey });
        queryClient.invalidateQueries({
          queryKey: getListInboundEmailsQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getListInboundMailboxesQueryKey(),
        });
      });

      // Keep the Logs page in sync. Send no longer mutates dashboard
      // rate-limit state, so there is nothing to patch on the summary.
      queryClient.invalidateQueries({ queryKey: getListEmailLogsQueryKey() });
    } catch (e) {
      // Roll back the optimistic entry: drop it from the cached thread,
      // clear the "sending…" indicator, and restore the form so the
      // user can retry without retyping their reply.
      queryClient.setQueryData<InboundEmailDetail>(detailKey, (prev) =>
        prev
          ? {
              ...prev,
              thread: {
                ...prev.thread,
                messages: prev.thread.messages.filter(
                  (m) => m.id !== optimisticId,
                ),
              },
            }
          : prev,
      );
      setPendingOptimisticIds((prev) => {
        if (!prev.has(optimisticId)) return prev;
        const next = new Set(prev);
        next.delete(optimisticId);
        return next;
      });
      setExpandedThreadKeys((prev) => {
        if (!prev) return prev;
        const next = new Set(prev);
        next.delete(`outbound-${optimisticId}`);
        return next;
      });
      setReplyOpen(true);
      setReplySubject(draftSnapshot.subject);
      setReplyBody(draftSnapshot.body);
      setReplyAiDrafted(draftSnapshot.aiDrafted);
      setReplyAttachments(draftSnapshot.attachments);

      toast.error(apiErrorMessage(e, "Failed to send reply"));
    }
  };

  const mailboxStatus = mailboxesQuery.data?.mailboxes.find(
    (m) => m.id === email.mailboxId,
  )?.status;

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden bg-background">
      <div className="h-16 px-6 border-b border-border flex items-center justify-between shrink-0 bg-card z-10 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/app/inbox")}
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Inbox
          </Button>
          <h1 className="font-bold text-lg truncate">
            {email.subject || "(no subject)"}
          </h1>
          {isUnread && (
            <Badge variant="default" className="shrink-0">
              Unread
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleRead}
            disabled={setReadMutation.isPending}
            data-testid="inbox-detail-toggle-read"
          >
            {isUnread ? (
              <>
                <MailOpen className="w-4 h-4 mr-1.5" />
                Mark read
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-1.5" />
                Mark unread
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={openInlineReply}
            data-testid="inbox-detail-reply"
          >
            <Reply className="w-4 h-4 mr-1.5" />
            Reply
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReplyInCompose}
            data-testid="inbox-detail-reply-in-compose"
            title="Open the full Compose page (lets you pick a template, add attachments, and preview)"
          >
            Reply in Compose
          </Button>
          <Select
            value={replyTone}
            onValueChange={(v) => setReplyTone(v as ReplyTone)}
          >
            <SelectTrigger
              className="h-8 w-[120px] text-xs"
              data-testid="inbox-detail-ai-tone"
              title="Tone for the AI-drafted reply"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REPLY_TONE_VALUES.map((v) => (
                <SelectItem key={v} value={v}>{REPLY_TONE_LABELS[v]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={handleAiReply}
            disabled={aiPending}
            data-testid="inbox-detail-ai-reply"
          >
            {aiPending ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-1.5" />
            )}
            Draft reply
          </Button>
          <AiRateLimitInline />
        </div>
      </div>

      <AlertDialog
        open={confirmRegenerate}
        onOpenChange={setConfirmRegenerate}
      >
        <AlertDialogContent data-testid="inbox-detail-ai-regenerate-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace your draft?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsent text in the reply panel. Generating a new AI
              draft will overwrite the subject and body.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my draft</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmRegenerate(false);
                void runAiReplyDraft();
              }}
              data-testid="inbox-detail-ai-regenerate-confirm-yes"
            >
              Replace with AI draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {mailboxStatus === "pending" && (
        <div className="px-6 py-2 border-b border-amber-500/30 bg-amber-500/10 text-xs text-amber-900 dark:text-amber-200 shrink-0">
          Heads up: mailbox{" "}
          <span className="font-mono">{email.mailboxAddress}</span> is still
          marked <span className="font-medium">pending</span> — check Brand
          Settings to finish setup.
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <div className="bg-card border border-border rounded-lg p-5 space-y-2">
            <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-1.5 text-sm">
              <div className="text-muted-foreground">From</div>
              <div className="font-medium flex items-center gap-1.5 flex-wrap">
                <span>
                  {email.fromName ? `${email.fromName} ` : ""}
                  <span className="font-mono text-xs text-muted-foreground">
                    &lt;{email.fromAddress}&gt;
                  </span>
                </span>
                <VerifiedSenderBadge
                  verificationStatus={email.verificationStatus ?? null}
                  testIdSuffix="-inbox-detail"
                />
                <InboxDetailFailedVerification
                  fromAddress={email.fromAddress}
                  verificationStatus={email.verificationStatus ?? null}
                  verificationDetails={email.verificationDetails ?? null}
                />
              </div>
              <div className="text-muted-foreground">To</div>
              <div className="font-mono text-xs">{email.toAddress}</div>
              <div className="text-muted-foreground">Mailbox</div>
              <div className="font-mono text-xs">{email.mailboxAddress}</div>
              <div className="text-muted-foreground">Received</div>
              <div className="text-xs">
                {new Date(email.receivedAt).toLocaleString()}
              </div>
              {email.providerMessageId && (
                <>
                  <div className="text-muted-foreground">Message-ID</div>
                  <div className="font-mono text-[11px] text-muted-foreground truncate">
                    {email.providerMessageId}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Full thread, chronological. Inbound messages render
              like the original single-message view (sandboxed iframe
              for HTML, pre-wrapped text fallback). Outbound replies
              render visually distinct (right-aligned accent stripe,
              "Sent" label) so users can tell at a glance which
              messages are theirs. */}
          <div
            className="space-y-4"
            data-testid="inbox-detail-thread"
            data-thread-count={email.thread.messages.length}
          >
            {email.thread.messages.map((m, idx) => {
              const isOutbound = m.kind === "outbound";
              const key = `${m.kind}-${m.id}`;
              const isExpanded = expandedThreadKeys?.has(key) ?? false;
              // Optimistic outbound messages have a negative id and are
              // tracked in `pendingOptimisticIds` until the server
              // confirms the send. Surface a discreet "Sending…"
              // indicator so users can tell the difference between a
              // reply that's been accepted by the server and one
              // that's still in flight.
              const isPendingOptimistic =
                isOutbound && pendingOptimisticIds.has(m.id);
              // Snippet for the collapsed view: prefer plain text;
              // fall back to a tag-stripped version of the HTML so
              // recipients of HTML-only senders still see something
              // meaningful before expanding.
              const rawSnippet =
                (m.textBody && m.textBody.trim().length > 0
                  ? m.textBody
                  : m.htmlBody
                    ? m.htmlBody.replace(/<[^>]*>/g, " ")
                    : ""
                )
                  .replace(/\s+/g, " ")
                  .trim();
              const snippet =
                rawSnippet.length > 140
                  ? `${rawSnippet.slice(0, 140)}…`
                  : rawSnippet;
              return (
                <div
                  key={key}
                  className={`border rounded-lg overflow-hidden ${
                    isOutbound
                      ? "bg-primary/5 border-primary/30 ml-8"
                      : "bg-white border-border"
                  }`}
                  data-testid={`thread-message-${idx}`}
                  data-thread-message-kind={m.kind}
                  data-thread-message-expanded={isExpanded ? "true" : "false"}
                  data-thread-message-pending={
                    isPendingOptimistic ? "true" : "false"
                  }
                >
                  {/* Whole header row toggles expand/collapse so
                      hitting anywhere on the meta strip works — same
                      gmail-style affordance users already expect. */}
                  <button
                    type="button"
                    onClick={() => toggleThreadMessage(key)}
                    aria-expanded={isExpanded}
                    aria-controls={`thread-body-${idx}`}
                    data-testid={`thread-message-toggle-${idx}`}
                    className={`w-full text-left px-4 py-2 border-b text-[11px] flex items-center gap-2 transition-colors hover:bg-muted/40 ${
                      isOutbound
                        ? "border-primary/20 bg-primary/10 text-primary hover:bg-primary/15"
                        : "border-border bg-muted/30 text-muted-foreground"
                    }`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                    )}
                    {isOutbound ? (
                      <Send className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                      <ArrowDownRight className="w-3.5 h-3.5 shrink-0" />
                    )}
                    <span className="font-medium uppercase tracking-widest shrink-0">
                      {isOutbound
                        ? isPendingOptimistic
                          ? "Sending"
                          : "Sent"
                        : "Received"}
                    </span>
                    {isPendingOptimistic && (
                      <span
                        className="inline-flex items-center gap-1 text-primary/80 normal-case tracking-normal shrink-0"
                        data-testid={`thread-message-sending-${idx}`}
                      >
                        <Loader2 className="w-3 h-3 animate-spin" />
                        sending…
                      </span>
                    )}
                    <span className="text-muted-foreground/80 truncate min-w-0">
                      {m.fromName ? `${m.fromName} ` : ""}
                      <span className="font-mono">
                        &lt;{m.fromAddress}&gt;
                      </span>
                      {" → "}
                      <span className="font-mono">{m.toAddress}</span>
                    </span>
                    {!isOutbound && (
                      <>
                        <VerifiedSenderBadge
                          verificationStatus={m.verificationStatus ?? null}
                          testIdSuffix={`-thread-${idx}`}
                        />
                        <InboundThreadFailedVerification
                          fromAddress={m.fromAddress}
                          verificationStatus={m.verificationStatus ?? null}
                          verificationDetails={m.verificationDetails ?? null}
                          testIdSuffix={`-thread-${idx}`}
                        />
                      </>
                    )}
                    <span className="ml-auto text-muted-foreground shrink-0">
                      {new Date(m.sentAt).toLocaleString()}
                    </span>
                  </button>
                  {!isExpanded && (
                    /* Collapsed preview: one-line snippet so users can
                        skim the conversation without expanding each
                        message. Click anywhere on the row above to
                        open. */
                    <div
                      className="px-4 py-3 text-sm text-muted-foreground truncate cursor-pointer hover:bg-muted/20"
                      onClick={() => toggleThreadMessage(key)}
                      data-testid={`thread-message-snippet-${idx}`}
                    >
                      {snippet || (
                        <span className="italic">(Empty body)</span>
                      )}
                    </div>
                  )}
                  {isExpanded && (
                    <div id={`thread-body-${idx}`}>
                      {m.htmlBody ? (
                        <iframe
                          title={`${m.kind} message ${m.id}`}
                          // allow-popups + allow-popups-to-escape-sandbox lets
                          // rewritten target="_blank" links open in a new tab
                          // without granting script execution, form submission,
                          // top-level navigation, or plugin loading. Crucial
                          // because inbound HTML is from arbitrary senders.
                          sandbox="allow-popups allow-popups-to-escape-sandbox"
                          srcDoc={rewriteLinksForPreview(m.htmlBody)}
                          className="w-full bg-white border-0"
                          style={{ minHeight: "40vh" }}
                        />
                      ) : m.textBody ? (
                        <pre className="p-5 whitespace-pre-wrap text-sm font-sans leading-relaxed">
                          {m.textBody}
                        </pre>
                      ) : (
                        <div className="p-5 text-sm text-muted-foreground italic">
                          (Empty body)
                        </div>
                      )}
                      {m.attachments.length > 0 && (
                        <div className="px-4 py-2 border-t border-border bg-muted/20 text-xs text-muted-foreground flex items-center gap-2">
                          <Paperclip className="w-3 h-3" />
                          {m.attachments.length} attachment
                          {m.attachments.length === 1 ? "" : "s"}
                        </div>
                      )}
                      {isOutbound && !isPendingOptimistic && (
                        /* Outbound replies live in email_logs — let
                            the user jump to the full log detail
                            (delivery status, provider error, “Use as
                            new email”, etc.) without losing their
                            place in the conversation. The link sits
                            outside the header <button> on purpose to
                            avoid nesting interactive elements. Hidden
                            for optimistic entries because their id is
                            a synthetic negative placeholder — the real
                            email_logs row doesn't exist yet, so the
                            link would 404. The next refetch swaps in
                            the real id and the link reappears. */
                        <div className="px-4 py-2 border-t border-primary/20 bg-primary/5 text-xs flex items-center justify-end">
                          <Link
                            href={`/app/logs/${m.id}`}
                            className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium"
                            data-testid={`thread-message-open-log-${idx}`}
                          >
                            View full log
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {replyOpen && (
            <div
              id="inline-reply-panel"
              data-testid="inline-reply-panel"
              className="bg-card border border-border rounded-lg p-5 space-y-4"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Reply className="w-4 h-4 text-primary" />
                  Reply inline
                  {replyAiDrafted && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] uppercase tracking-wider"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI draft
                    </Badge>
                  )}
                </h3>
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      // Explicit discard: drop the saved row AND wipe
                      // the inline form so reopening starts from the
                      // default quoted-body prefill again.
                      await replyDraft.clearDraft();
                      setReplySubject("");
                      setReplyBody("");
                      setReplyAiDrafted(false);
                      setShowRepDefaultsNudge(false);
                      setReplyAttachments([]);
                      setReplyOpen(false);
                      toast.success("Draft discarded");
                    }}
                    data-testid="inline-reply-discard"
                  >
                    Discard draft
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Cancel just hides the panel — the saved draft
                      // stays put so reopening restores it.
                      setReplyOpen(false);
                      setShowRepDefaultsNudge(false);
                      setReplyAttachments([]);
                    }}
                    data-testid="inline-reply-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-2 text-xs items-center">
                <div className="text-muted-foreground">To</div>
                <div className="font-mono">{email.fromAddress}</div>
                <div className="text-muted-foreground">Reply-To</div>
                <div className="font-mono">{email.mailboxAddress}</div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="inline-reply-subject" className="text-xs">
                  Subject
                </Label>
                <Input
                  id="inline-reply-subject"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  data-testid="inline-reply-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inline-reply-body" className="text-xs">
                  Message
                </Label>
                <Textarea
                  id="inline-reply-body"
                  value={replyBody}
                  onChange={(e) => {
                    setReplyBody(e.target.value);
                    // Once the user edits the AI draft, drop the badge —
                    // the message is now their own words, not the model's.
                    if (replyAiDrafted) setReplyAiDrafted(false);
                  }}
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="inline-reply-body"
                />
                {showRepDefaultsNudge && replyAiDrafted && (
                  <p
                    className="text-[11px] text-amber-700 dark:text-amber-300 flex items-center gap-1"
                    data-testid="inline-reply-rep-defaults-nudge"
                  >
                    <span>
                      Your AI draft is missing a personal sign-off.
                    </span>
                    <Link
                      href="/app/representative"
                      className="underline hover:no-underline"
                    >
                      Add your name, title, and phone →
                    </Link>
                  </p>
                )}
                {!email.inReplyTo && (
                  <p className="text-[11px] text-amber-700 dark:text-amber-300">
                    No threading headers were found on the inbound message —
                    the reply will still send, but the recipient's email
                    client may show it as a new conversation.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Attachments</Label>
                <AttachmentsField
                  attachments={replyAttachments}
                  onChange={setReplyAttachments}
                  testIdPrefix="inline-reply-attachments"
                  helperText="Drop a PDF or image (PNG, JPEG, GIF, WebP) here or click to attach. Up to 25 MB total."
                />
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button
                  size="sm"
                  onClick={handleSendInlineReply}
                  disabled={sendMutation.isPending}
                  data-testid="inline-reply-send"
                >
                  {sendMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-1.5" />
                  )}
                  Send reply
                </Button>
              </div>
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            <Link
              href="/app/inbox"
              className="underline hover:text-foreground"
            >
              ← Back to inbox
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function InboxDetailFailedVerification({
  fromAddress,
  verificationStatus,
  verificationDetails,
}: {
  fromAddress: string;
  verificationStatus: VerificationStatus | null;
  verificationDetails: VerificationDetails | null;
}) {
  const { isTrusted, trustSender, isTrusting } = useTrustedSenderCheck(
    fromAddress,
    verificationStatus,
  );

  if (verificationStatus !== "fail" || isTrusted) return null;

  return (
    <span className="inline-flex items-center gap-1.5">
      <FailedVerificationWarning
        verificationStatus={verificationStatus}
        verificationDetails={verificationDetails}
        testIdSuffix="-inbox-detail"
      />
      <button
        onClick={trustSender}
        disabled={isTrusting}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:opacity-50"
        data-testid="inbox-detail-trust-sender"
      >
        <ShieldAlert className="h-3 w-3" />
        {isTrusting ? "Trusting…" : "Trust this sender"}
      </button>
    </span>
  );
}

function InboundThreadFailedVerification({
  fromAddress,
  verificationStatus,
  verificationDetails,
  testIdSuffix,
}: {
  fromAddress: string;
  verificationStatus: VerificationStatus | null;
  verificationDetails: VerificationDetails | null;
  testIdSuffix: string;
}) {
  const { isTrusted } = useTrustedSenderCheck(fromAddress, verificationStatus);
  if (isTrusted) return null;
  return (
    <FailedVerificationWarning
      verificationStatus={verificationStatus}
      verificationDetails={verificationDetails}
      testIdSuffix={testIdSuffix}
    />
  );
}
